import { memo } from 'react';
import { Card } from '@bancobarigui/barihaus';
import { Checkbox, Tooltip } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import type { AmostraCardProps } from './types';
import { useAmostraModal } from './hooks';
import { AmostraImage } from './components/AmostraImage';
import { AmostraInfo } from './components/AmostraInfo';
import { AmostraActions } from './components/AmostraActions';
import { AmostraModal } from './components/AmostraModal';
import * as styles from './styles';

// Cores discretas para ranking - apenas até posição 3, depois neutro
const RANKING_COLORS: Record<number, { bg: string; border: string; text: string }> = {
  1: { bg: '#fffbe6', border: '#faad14', text: '#d48806' }, // Ouro
  2: { bg: '#f5f5f5', border: '#8c8c8c', text: '#595959' }, // Prata
  3: { bg: '#fff7e6', border: '#d4a574', text: '#ad6800' }, // Bronze
};

// Cor para posições 4+ (azul claro, diferente do prata)
const NEUTRAL_COLOR = { bg: '#e6f4ff', border: '#91caff', text: '#0958d9' };

function AmostraCardComponent({
  amostra,
  onEdit,
  onDelete,
  metodologia,
  fotoFachada,
  hideActions,
  showSelectionCheckbox,
  onSelectionChange,
  showRanking,
  isDragging,
}: AmostraCardProps) {
  const { isModalOpen, setIsModalOpen, form, handleEdit, handleDelete, handleSave } =
    useAmostraModal(amostra, onEdit, onDelete);

  const isAvaliando = amostra.identificacao === 'Avaliando';
  const ranking = amostra.rankingHomogeneizacao;
  const rankingColor = ranking ? (RANKING_COLORS[ranking] || NEUTRAL_COLOR) : null;
  const isSelected = amostra.selecionadaHomogeneizacao ?? false;

  return (
    <>
      <Card
        style={{
          ...styles.cardStyle,
          ...(isDragging ? {
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          } : {}),
          ...(isSelected ? {
            borderColor: '#52c41a',
            borderWidth: 2,
            backgroundColor: '#f6ffed',
          } : {})
        }}
        bodyStyle={styles.cardBodyStyle}
      >
        <div style={styles.cardContentStyle}>
          {/* Handle de drag quando ranking está habilitado */}
          {showRanking && (
            <div
              className="drag-handle"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 8,
                cursor: 'grab',
                color: '#bfbfbf',
                padding: '4px 0',
              }}
            >
              <HolderOutlined style={{ fontSize: 18 }} />
            </div>
          )}

          {/* Badge de ranking discreto */}
          {showRanking && ranking && (
            <Tooltip title={`Posição ${ranking} no ranking`}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 24,
                height: 24,
                borderRadius: 4,
                backgroundColor: rankingColor?.bg,
                border: `1px solid ${rankingColor?.border}`,
                color: rankingColor?.text,
                fontWeight: 600,
                fontSize: 12,
                marginRight: 8,
              }}>
                {ranking}º
              </div>
            </Tooltip>
          )}

          {/* Checkbox de seleção para homogeneização */}
          {showSelectionCheckbox && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginRight: 12,
              paddingRight: 12,
              borderRight: '1px solid #f0f0f0'
            }}>
              <Checkbox
                checked={isSelected}
                onChange={(e) => onSelectionChange?.(amostra.identificacao, e.target.checked)}
              />
            </div>
          )}

          <AmostraImage fonte={amostra.fonte} identificacao={amostra.identificacao} urlOriginal={amostra.urlOriginal} fotoFachada={fotoFachada} imagemUrl={amostra.imagemUrl} externalId={amostra.externalId} />
          <AmostraInfo amostra={amostra} metodologia={metodologia} />
          {!hideActions && (
            <AmostraActions isAvaliando={isAvaliando} urlOriginal={amostra.urlOriginal} onEdit={handleEdit} onDelete={handleDelete} />
          )}
        </div>
      </Card>

      <AmostraModal
        amostra={amostra}
        isOpen={isModalOpen}
        form={form}
        onSave={handleSave}
        onCancel={() => setIsModalOpen(false)}
        metodologia={metodologia}
      />

      <style>{styles.mediaQueryStyles}</style>
    </>
  );
}

export const AmostraCard = memo(AmostraCardComponent);
